import type {
  Permission,
  PermissionScope,
  ScopedPermission,
} from "@pulsar-anvil-studios/api-types";

export const NAMESPACE_ORDER = [
  "game",
  "chat",
  "user",
  "social",
  "party",
  "auth",
  "mod",
  "admin",
  "system",
] as const;

const ALL_PERMISSIONS: Permission[] = [
  "game:read",
  "game:play",
  "game:create_lobby",
  "game:join",
  "chat:send",
  "chat:read",
  "user:read",
  "user:write",
  "user:read_self",
  "user:update_self",
  "user:manage_permissions",
  "social:read",
  "social:write",
  "social:friend_request",
  "social:friend_accept",
  "party:use",
  "auth:read_self",
  "mod:warn",
  "mod:mute",
  "mod:kick",
  "mod:view_reports",
  "mod:close_reports",
  "admin:ban",
  "admin:unban",
  "admin:edit_user",
  "admin:list_users",
  "admin:view_user",
  "admin:search_users",
  "admin:view_logs",
  "admin:manage_games",
  "admin:ws_connections",
  "admin:origin_rotate",
  "system:manage_roles",
  "system:manage_admins",
  "system:config",
];

export function getNamespace(permission: Permission): string {
  return permission.split(":")[0];
}

export function getAction(permission: Permission): string {
  return permission.split(":")[1];
}

export const PERMISSION_NAMESPACES: Record<string, Permission[]> = Object.fromEntries(
  NAMESPACE_ORDER.map((namespace) => [
    namespace,
    ALL_PERMISSIONS.filter((permission) => getNamespace(permission) === namespace),
  ]),
) as Record<string, Permission[]>;

/** Groups permissions by name for O(1) lookup. */
export function buildPermissionMap(
  permissions: ScopedPermission[],
): Map<string, ScopedPermission[]> {
  const map = new Map<string, ScopedPermission[]>();
  for (const scopedPermission of permissions) {
    const entries = map.get(scopedPermission.permission);
    if (entries) {
      entries.push(scopedPermission);
    } else {
      map.set(scopedPermission.permission, [scopedPermission]);
    }
  }
  return map;
}

/** Check if granted IDs cover all requested IDs. `null` on either side = wildcard. */
export function dimensionCovers(
  granted: string[] | null,
  requested: string[] | null | undefined,
): boolean {
  if (!requested) {
    return true;
  }
  if (!granted) {
    return true;
  }
  return requested.every((id) => granted.includes(id));
}

/** Evaluate whether a set of permissions is satisfied (any or all). */
export function evaluateAuthorization(
  hasPermission: (p: Permission, ctx?: Partial<PermissionScope>) => boolean,
  permissions: Permission[],
  options?: { mode?: "all" | "any"; scope?: Partial<PermissionScope> },
): boolean {
  if (permissions.length === 0) {
    return true;
  }
  const { mode = "any", scope } = options ?? {};
  const check = (permission: Permission) => hasPermission(permission, scope);
  return mode === "all" ? permissions.every(check) : permissions.some(check);
}

/** Check if a permission is granted, optionally with scope context. */
export function checkPermission(
  map: Map<string, ScopedPermission[]>,
  permission: Permission,
  context?: Partial<PermissionScope>,
): boolean {
  const entries = map.get(permission);
  if (!entries) {
    return false;
  }
  return entries.some((scopedPermission) => {
    if (!scopedPermission.scope) {
      return true;
    }
    if (!context) {
      return false;
    }
    return (
      dimensionCovers(scopedPermission.scope.gameIds, context.gameIds) &&
      dimensionCovers(scopedPermission.scope.userIds, context.userIds) &&
      dimensionCovers(scopedPermission.scope.regions, context.regions)
    );
  });
}

/** Lightweight React key for a ScopedPermission — avoids array-index keys. */
export function scopedPermissionKey(sp: ScopedPermission): string {
  if (!sp.scope) {
    return sp.permission;
  }
  const g = sp.scope.gameIds?.join(",") ?? "";
  const u = sp.scope.userIds?.join(",") ?? "";
  const r = sp.scope.regions?.join(",") ?? "";
  return `${sp.permission}|${g}|${u}|${r}`;
}
